/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./ConfirmPayment.css";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { simpleFetch } from "../../restWrapper";
import errorMsg from "../../assets/json/errMsg.json";
import { getOrganizationID, resolveThemeColors } from "../../assets/helper";
import checkplusLogo from "../../assets/component/images/checkplus-logo.svg";
import plugLogo from "../../assets/component/images/plug-logo.svg";
import { PLATFORM } from "../../config";

// Manual payment methods: their receipts are issued once the org approves
// the transaction, so ConfirmPayment never waits for one
const manualPaymentMethods = [
  "alipay",
  "alipay_hk",
  "alipay_ch",
  "wechatpay",
  "wechatpay_hk",
  "wechatpay_ch",
  "bocpay",
  "octopus",
  "fps",
  "bank_transfer",
  "check_writing",
  "cash",
  "paypal_manual",
];

class ConfirmPayment extends Component {
  constructor(props) {
    super(props);
    try {
      let objectId;
      objectId = window.location.pathname.split("/")[2];

      // const stateObj = JSON.parse(localStorage.getItem("state"));
      
      const subdomain = getOrganizationID(window.location.href);
          
      if (this.props.location.state) {
        this.state = this.props.location.state;
      } else {
        this.state = {
          subdomain: subdomain,
          objectId: objectId,
          loading: true,
          error: false,
          pollTimedOut: false,
          PayMePaymentRequestId: "",
          organization: {
            headerColor: "",
            buttonColor: "",
            progressBarColor: "",
            logo: "",
            organizationName: "",
          },
        };
      }
    } catch (e) {
      this.props.history.push("/SelectMission");
    }
  }

  componentDidMount() {
    this.unmounted = false;

    // Only fatal when the finalize call was our sole data source (mobile
    // PayMe return); the desktop flow already has everything in router state
    const needsFinalizeData = this.state.loading === true;
    // Review/QR has already established payment success. Receipt reassurance
    // must not wait for a slow finalize response before starting its clock.
    if (!needsFinalizeData && this.awaitsReceipt()) this.startReceiptWaitTimer();

    if (this.state.objectId) {
      /* If there is no data other than the objectId from the domain, use objectId to fetch payment data */
      simpleFetch("/user/payments/finalizePayMeLink", "POST", {
        objectId: this.state.objectId,
      }).then((res) => {
        if (this.unmounted) return;
        if (!res || res.status !== 200 || !res.body || !res.body.organization) {
          if (needsFinalizeData) {
            this.setState({ loading: false, error: true });
          }
          return;
        }

        const PayMePaymentRequestId = res.body.PayMePaymentRequestId;
        const receiptLink = res.body.receiptLink;
        const organization = res.body.organization;

        const logoStyle = {
          backgroundImage: "url('" + organization.logo + "')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "contain",
          border: "none",
        };

        this.setState({
          expired: res.body.status === 'expired' ||
            (this.state.expired === true && res.body.status !== 'complete'),
          PayMePaymentRequestId: PayMePaymentRequestId,
          autoReceipt: res.body.autoReceipt !== undefined ? res.body.autoReceipt : this.state.autoReceipt,
          organization: {
            headerColor: organization.headerColor,
            buttonColor: organization.buttonColor,
            progressBarColor: organization.progressBarColor,
            logo: logoStyle,
            organizationName: organization.organizationName,
            // Keep the org-URL fallback for the return button — this object
            // REPLACES any organization from navigation state (CP-240).
            organizationUrl:
              organization.organizationUrl ||
              (this.state.organization && this.state.organization.organizationUrl),
          },
          // The mobile PayMe return lands here with no navigation state, so
          // the project's return link only exists in this response. Dropping
          // it meant the return button ignored the org's configured redirect
          // for every mobile PayMe donor (CP-240).
          projectReturnUrl:
            this.state.projectReturnUrl || res.body.projectReturnUrl,
          loading: false,
        }, () => {
          // Callback ensures state is set before setting up interval (React 18 fix)
          if (!receiptLink && !this.state.expired && this.state.autoReceipt !== false) {
            this.pollForReceipt((attempt) => ({
              objectId: this.state.objectId,
              // Ask the backend to verify settlement with PayMe directly
              // (covers a lost webhook) — every 5th poll to limit API load
              checkSettlement: attempt % 5 === 1,
            }));
          } else {
            /* If a receipt is found from the payment, use information in ConfirmPayment. */
            this.setState({
              receiptLink: receiptLink,
            });
          }
        });
      }).catch((err) => {
        if (this.unmounted) return;
        // console.log(err);
        if (needsFinalizeData) {
          this.setState({ loading: false, error: true });
        }
      });
    } else if (needsFinalizeData) {
      // Landed here without an objectId in the URL — nothing to fetch
      this.setState({ loading: false, error: true });
    } else if (this.awaitsReceipt()) {
      // Card payments (CP-406): the receipt wasn't ready when the payment
      // replied. Keep checking for it by the Stripe intent id; with nothing
      // to check by, say it will arrive by email rather than load forever.
      if (this.state.paymentIntent) {
        this.pollForReceipt(() => ({ paymentIntent: this.state.paymentIntent }));
      } else {
        this.setState({ pollTimedOut: true });
      }
    }
  }

  // A payment that arrived without its receipt, from an org that issues
  // receipts automatically. Scheduled payments get theirs after the first
  // charge, and manual methods once the org approves them.
  awaitsReceipt() {
    const { receiptLink, autoReceipt, isRecurring, scheduledDate, brand, paymentIntent } = this.state;
    return (
      !receiptLink &&
      !this.state.expired &&
      autoReceipt !== false &&
      !(isRecurring && scheduledDate && !paymentIntent) &&
      !manualPaymentMethods.includes(brand)
    );
  }

  startReceiptWaitTimer() {
    if (this.receiptWaitTimer || this.state.receiptDelayed) return;
    this.receiptWaitTimer = setInterval(() => {
      clearInterval(this.receiptWaitTimer);
      this.receiptWaitTimer = null;
      if (this.awaitsReceipt()) this.setState({ receiptDelayed: true });
    }, 30000);
  }

  // Reassure the donor after 30 seconds, while retaining the bounded
  // five-minute receipt poll so a later PDF still becomes downloadable.
  pollForReceipt(lookup) {
    this.startReceiptWaitTimer();
    this.pollAttempts = 0;
    this.fetchPaymentStatus = setInterval(() => {
      this.pollAttempts += 1;
      if (this.pollAttempts > 50) {
        clearInterval(this.fetchPaymentStatus);
        this.fetchPaymentStatus = null;
        this.setState({ pollTimedOut: true });
        return;
      }
      simpleFetch("/user/payments/fetchPaymentStatus/", "POST", lookup(this.pollAttempts))
        .then((res) => {
          if (this.unmounted) return;
          if (res?.body?.result?.status === 'expired') {
            clearInterval(this.fetchPaymentStatus);
            this.fetchPaymentStatus = null;
            this.setState({ expired: true });
          } else if (res?.body?.result?.autoReceipt === false) {
            clearInterval(this.fetchPaymentStatus);
            this.fetchPaymentStatus = null;
            this.setState({ autoReceipt: false });
          } else if (res && res.body && res.body.result && res.body.result.receiptUrl) {
            clearInterval(this.fetchPaymentStatus);
            this.fetchPaymentStatus = null;
            this.setState({
              receiptLink: res.body.result.receiptUrl,
            });
          }
        })
        .catch(() => {
          /* transient poll failure — next tick retries */
        });
    }, 6000);
  }

  componentWillUnmount() {
    this.unmounted = true;
    clearInterval(this.receiptWaitTimer);
    this.receiptWaitTimer = null;
    if (this.fetchPaymentStatus) {
      clearInterval(this.fetchPaymentStatus);
      this.fetchPaymentStatus = null;
    }
  }

  getIsFreePlanFlow() {
    const { isFreePlanFlow, mission, organization } = this.state;
    if (typeof isFreePlanFlow === 'boolean') {
      return isFreePlanFlow;
    }
    if (mission && mission.status === 'unlisted') {
      return true;
    }
    if (organization && organization.currentPlan === 'free') {
      return true;
    }
    return false;
  }

  render() {

    const organization = this.state.organization || {};
    const normalizeHex = (value, fallback) => {
      if (!value) {
        return fallback;
      }
      return value.startsWith("#") ? value : `#${value}`;
    };

    const isFreePlanFlow = this.getIsFreePlanFlow();

    const defaultHeaderHex = normalizeHex(organization.headerColor || "", "#1B63B7");
    const defaultButtonHex = normalizeHex(organization.buttonColor || "", "#FFFFFF");

    const resolved = resolveThemeColors(
      organization.headerColor || "1B63B7",
      organization.buttonColor || "FFFFFF"
    );
    const resolvedHeaderHex = isFreePlanFlow ? "#4A9FE9" : normalizeHex(resolved.headerColor, "#1B63B7");
    const resolvedButtonHex = isFreePlanFlow ? "#FFFFFF" : normalizeHex(resolved.buttonColor, "#FFFFFF");

    const headerHex = isFreePlanFlow ? "#4A9FE9" : defaultHeaderHex;
    const buttonHex = isFreePlanFlow ? "#FFFFFF" : defaultButtonHex;
    const accentTextHex = isFreePlanFlow ? "#1F2A37" : resolvedButtonHex;

    const viewClassName = isFreePlanFlow
      ? "ConfirmPayment-view ConfirmPayment-view--free"
      : "ConfirmPayment-view";

    const mainContainerClassName = isFreePlanFlow
      ? "ConfirmPayment-mainContainer ConfirmPayment-mainContainer--free"
      : "ConfirmPayment-mainContainer";

    const messageClassName = isFreePlanFlow
      ? "ConfirmPayment-message ConfirmPayment-message--free"
      : "ConfirmPayment-message";

    const messageContainerClassName = isFreePlanFlow
      ? "ConfirmPayment-messageContainer ConfirmPayment-messageContainer--free"
      : "ConfirmPayment-messageContainer";

    const footerClassName = isFreePlanFlow
      ? "ConfirmPayment-footer ConfirmPayment-footer--free"
      : "ConfirmPayment-footer";

    const nextBtnClassName = isFreePlanFlow
      ? "ConfirmPayment-nextBtn ConfirmPayment-nextBtn--free"
      : "ConfirmPayment-nextBtn";

    const receiptBtnClassName = isFreePlanFlow
      ? "ConfirmPayment-DownloadReceiptBtn ConfirmPayment-DownloadReceiptBtn--free"
      : "ConfirmPayment-DownloadReceiptBtn";

    const manualMessageClassName = isFreePlanFlow
      ? "ConfirmPayment-manualPaymentMessage ConfirmPayment-manualPaymentMessage--free"
      : "ConfirmPayment-manualPaymentMessage";

    const manualMessageTitleClassName = isFreePlanFlow
      ? "ConfirmPayment-manualPaymentMessageTitle ConfirmPayment-manualPaymentMessageTitle--free"
      : "ConfirmPayment-manualPaymentMessageTitle";

    const manualMessageBodyClassName = isFreePlanFlow
      ? "ConfirmPayment-manualPaymentMessageBody ConfirmPayment-manualPaymentMessageBody--free"
      : "ConfirmPayment-manualPaymentMessageBody";

    const headerOverrideStyle = {
      background: resolvedHeaderHex,
    };

    const nextBtnStyle = {
      background: buttonHex,
      color: headerHex,
    };

    const receiptBtnStyle = isFreePlanFlow
      ? { background: "#FFFFFF", border: "1px solid #E5E7EB" }
      : { background: buttonHex, color: headerHex };

    const messageTextStyle = isFreePlanFlow ? undefined : { color: buttonHex };

    const viewStyle = isFreePlanFlow ? undefined : { background: headerHex };

    const rawLogo = organization.logo;
    let logoStyle = {};
    let hasLogo = false;
    if (rawLogo) {
      if (typeof rawLogo === "object") {
        logoStyle = rawLogo;
        hasLogo = Object.keys(rawLogo).length > 0;
      } else if (typeof rawLogo === "string") {
        const trimmed = rawLogo.trim();
        if (trimmed) {
          hasLogo = true;
          logoStyle = {
            backgroundImage: `url('${trimmed}')`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            backgroundPosition: "center",
          };
        }
      }
    }

    const logoClassName = isFreePlanFlow
      ? "ConfirmPayment-logo ConfirmPayment-logo--free"
      : "ConfirmPayment-logo";
    const logoFallbackText = hasLogo
      ? ""
      : organization.organizationName || "LOGO";

    // Apply organization theme color as background for free plan logos
    if (isFreePlanFlow && hasLogo) {
      // Use orgHeaderColor (actual organization color) if available, otherwise fall back to headerColor or default blue
      const actualOrgColor = organization.orgHeaderColor || organization.headerColor || "";
      const themeColor = normalizeHex(actualOrgColor, "#4A9FE9");
      logoStyle = {
        ...logoStyle,
        backgroundColor: themeColor,
        backgroundSize: "60%",
        backgroundPosition: "center"
      };
    }

    const languageObj = this.props.languageObj;

    if (this.state.error) {
      const langErrObj =
        (errorMsg[this.props.language] || errorMsg.en).ConfirmPayment ||
        errorMsg.en.ConfirmPayment;
      return (
        <div className="ConfirmPayment-processingView">
          <div className="ConfirmPayment-processingContainer">
            <div className="ConfirmPayment-processingPrompt">
              <div className="ConfirmPayment-processingMessage">
                {languageObj.errorTitle || "Something went wrong"}
              </div>
              <div className="ConfirmPayment-processingMessage2">
                {langErrObj.finalizeErr}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (this.state.expired) return <div className="ConfirmPayment-processingView">
      <div className="ConfirmPayment-processingContainer"><div className="ConfirmPayment-processingPrompt">
        <div className="ConfirmPayment-processingMessage">{languageObj.expiredTitle}</div>
        <div className="ConfirmPayment-processingMessage2">{languageObj.expiredMessage}</div>
        <Link to="/SelectMission">{languageObj.nextBtn}</Link>
      </div></div>
    </div>;

    if (this.state.loading && this.state.organization.logo !== null) {
      return (
        <div className="ConfirmPayment-processingView">
          <div className="ConfirmPayment-processingContainer">
            <div className="ConfirmPayment-processingPrompt">
              <div className="ConfirmPayment-processingLoad" />
              <div className="ConfirmPayment-processingMessage">
                {languageObj.loading[0]}
              </div>

              <div className="ConfirmPayment-processingMessage2">
                {languageObj.loading[1]}
              </div>
            </div>
          </div>
        </div>
      );
    }


    const messageSecondaryClassName = isFreePlanFlow
      ? "ConfirmPayment-message2 ConfirmPayment-message2--free"
      : "ConfirmPayment-message2";

    const DownloadReceiptBtn = () => {
      // Check if brand is in the state (passed from Review page)
      const isManualPayment = this.state.brand && manualPaymentMethods.includes(this.state.brand);
      const isScheduledPayment = this.state.isRecurring && this.state.scheduledDate && !this.state.paymentIntent && !this.state.receiptLink;
      const isAutoReceiptDisabled = this.state.autoReceipt === false;

      // If auto receipt is disabled, show pending approval message
      if (isAutoReceiptDisabled) {
        return (
          <div className={manualMessageClassName} data-testid="confirmpayment-download-receipt">
            <div className={manualMessageTitleClassName}>
              {languageObj.pendingReceiptTitle || "Payment Received"}
            </div>
            <div className={manualMessageBodyClassName}>
              {languageObj.pendingReceiptMessage || "Your receipt will be issued once the organization has confirmed and approved this transaction."}
            </div>
          </div>
        );
      }

      // If it's a manual payment, show a message instead of the download button
      if (isManualPayment) {
        return (
          <div className={manualMessageClassName} data-testid="confirmpayment-download-receipt">
            <div className={manualMessageTitleClassName}>
              {languageObj.manualPaymentTitle || "Manual Payment Received"}
            </div>
            <div className={manualMessageBodyClassName}>
              {languageObj.manualPaymentMessage || "Our team will review your transaction since this is paid manually. You will receive a confirmation email once the review is complete."}
            </div>
          </div>
        );
      }

      // If it's a scheduled recurring payment, show appropriate message
      if (isScheduledPayment) {
        const scheduledDateStr = new Date(this.state.scheduledDate).toLocaleDateString();
        return (
          <div className={manualMessageClassName} data-testid="confirmpayment-download-receipt">
            <div className={manualMessageTitleClassName}>
              {languageObj.scheduledPaymentTitle || "Recurring Payment Scheduled"}
            </div>
            <div className={manualMessageBodyClassName}>
              {languageObj.scheduledPaymentMessage
                ? languageObj.scheduledPaymentMessage.replace('{date}', scheduledDateStr)
                : `Your recurring payment has been scheduled and will start on ${scheduledDateStr}. You will receive a receipt after the first payment is processed.`
              }
            </div>
          </div>
        );
      }

      // After 30 seconds, reassure the donor while the receipt poll continues.
      if ((this.state.receiptDelayed || this.state.pollTimedOut) && !this.state.receiptLink) {
        return (
          <div className={manualMessageClassName} role="status" aria-live="polite">
            <div className={manualMessageTitleClassName}>
              {languageObj.receiptDelayedTitle || "Your receipt is on its way"}
            </div>
            <div className={manualMessageBodyClassName}>
              {languageObj.receiptDelayedMessage ||
                "This is taking longer than usual. Your receipt will be sent to your email once it's ready."}
            </div>
          </div>
        );
      }

      // For non-manual payments, show the download receipt button as before
      if (this.state.receiptLink) {
        return (
          <div
            className={receiptBtnClassName}
            style={receiptBtnStyle}
            data-testid="confirmpayment-download-receipt"
            onClick={() => {
              window.open(this.state.receiptLink, "_blank");
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                className="ConfirmPayment-receiptIcn"
                style={isFreePlanFlow ? { background: "#4A9FE9" } : { background: headerHex }}
                alt="Receipt"
              />
            </div>
            <div className="ConfirmPayment-receiptTxt">
              {languageObj.downloadReceipt}
            </div>
          </div>
        );
      } else
        return (
          <div className={manualMessageClassName} role="status" aria-live="polite"
            data-testid="confirmpayment-download-receipt">
            <div className={`${manualMessageTitleClassName} ConfirmPayment-receiptPreparingTitle`}>
              <span className="ConfirmPayment-receiptSpinner" aria-hidden="true" />
              {languageObj.receiptPreparingTitle || "Preparing your receipt"}
            </div>
            <div className={manualMessageBodyClassName}>
              {languageObj.receiptPreparingMessage || "We’re preparing your receipt. It will appear here when ready."}
            </div>
          </div>
        );
    };

    const RenderReturnBtn = () => {
      const projectReturnUrl = this.state.projectReturnUrl;
      const organizationUrl = this.state.organization.organizationUrl;

      // Check for valid URLs (non-empty after trimming)
      const validProjectUrl = projectReturnUrl && projectReturnUrl.trim();
      const validOrgUrl = organizationUrl && organizationUrl.trim();

      if (validProjectUrl || validOrgUrl) {
        const url = validProjectUrl || validOrgUrl;

        return (
          <div className={footerClassName}>
            <a href={url} data-testid="confirmpayment-next-btn">
              <div className={nextBtnClassName} style={nextBtnStyle}>
                {languageObj.nextBtn}
              </div>
            </a>
          </div>
        );
      }

      return (
        <div className={footerClassName}>
          <Link to="/SelectMission" data-testid="confirmpayment-next-btn">
            <div className={nextBtnClassName} style={nextBtnStyle}>
              {languageObj.nextBtn}
            </div>
          </Link>
        </div>
      );
    };

    const renderFreeHeader = () => (
      <div className="ConfirmPayment-header ConfirmPayment-header--free">
        <div className="ConfirmPayment-freeBrand">
          <span className="ConfirmPayment-freeBrandPowered">Powered by</span>
          <img
            src={PLATFORM === "plug" ? plugLogo : checkplusLogo}
            alt={PLATFORM === "plug" ? "Plug" : "CheckPlus for Nonprofits"}
            className="ConfirmPayment-freeBrandLogoImage"
          />
        </div>
        <div className="ConfirmPayment-freeHeaderNote">{languageObj.secureCheckoutComplete}</div>
      </div>
    );

    return (
      <div className={viewClassName} style={viewStyle}>
        <Helmet>
          <title>{this.state.organization.organizationName}</title>
        </Helmet>
        {isFreePlanFlow ? renderFreeHeader() : null}
        <div className={mainContainerClassName}>
          <div className={logoClassName} style={logoStyle}>
            {!hasLogo && logoFallbackText}
          </div>

          <div className={messageClassName}>
            <div className={messageContainerClassName} style={messageTextStyle}>
              <strong style={{ display: 'block', marginBottom: '20px' }}>{languageObj.message[0]}</strong>

              <DownloadReceiptBtn />

              {/* Only show message2 for non-manual payments with auto receipt enabled */}
              {(this.state.receiptLink || !this.awaitsReceipt()) && this.state.autoReceipt !== false && (!this.state.brand || !manualPaymentMethods.includes(this.state.brand)) ? (
                <div className={messageSecondaryClassName}>
                  {this.state.receiptLink
                    ? languageObj.message[1]
                    : languageObj.message[2]}
                </div>
              ) : null}

              <RenderReturnBtn />
            </div>
          </div>
        </div>
      </div>
    );
  }
}
ConfirmPayment.propTypes = {
  languageObj: PropTypes.object,
  language: PropTypes.string,
  isAuth: PropTypes.bool,
};
export default ConfirmPayment;
